import React, { useState, useEffect, useCallback } from 'react';
import { visualEditorApi } from '@/services/api/agentApi';

interface SharedPatternsSettingsProps {
  isAdmin: boolean;
}

/**
 * Admin-only settings panel for shared cross-organization design patterns.
 * Controls opt-in/opt-out for anonymized pattern sharing.
 */
export const SharedPatternsSettings: React.FC<SharedPatternsSettingsProps> = ({ isAdmin }) => {
  const [optedIn, setOptedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchOptInStatus = useCallback(async () => {
    try {
      const res = await visualEditorApi.getSharedPatternsOptIn();
      setOptedIn(res?.data?.optedIn || false);
    } catch {
      // Default to false
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOptInStatus();
  }, [fetchOptInStatus]);

  const handleToggle = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const newValue = !optedIn;
      await visualEditorApi.setSharedPatternsOptIn(newValue);
      setOptedIn(newValue);
    } catch {
      // Revert on error
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <div
      style={{
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
      }}
    >
      <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 600, color: '#111827' }}>
        Shared Design Patterns
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6B7280', lineHeight: 1.5 }}>
        When enabled, anonymized design patterns from your pages can be shared with other
        organizations to improve AI-generated layouts. No org-specific data, content, or
        branding is ever shared — only structural patterns (layout hierarchy, component
        arrangement, responsive breakpoints).
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={handleToggle}
          disabled={loading || saving}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '6px',
            border: `1px solid ${optedIn ? '#10B981' : '#D1D5DB'}`,
            background: optedIn ? '#ECFDF5' : '#FFFFFF',
            color: optedIn ? '#065F46' : '#374151',
            cursor: loading || saving ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: 500,
            opacity: loading || saving ? 0.6 : 1,
          }}
        >
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: optedIn ? '#10B981' : '#D1D5DB',
            }}
          />
          {loading ? 'Loading...' : saving ? 'Saving...' : optedIn ? 'Opted In' : 'Opted Out'}
        </button>
        <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
          {optedIn
            ? 'Your org contributes to and benefits from shared patterns'
            : 'Click to enable shared pattern intelligence'}
        </span>
      </div>
    </div>
  );
};
